import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import type React from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { PanelGroup } from './types';

/**
 * Photoshop-style auto-collapse (accordion) + always-fill layout for a
 * vertical panel column.
 *
 * Rules implemented:
 * - The column always fills its available height exactly: the bottom-most
 * group extends all the way to the end of the side panel. Groups never
 * overflow the container and never leave an unfilled gap at the bottom.
 * - Groups without a user-defined height (isCustomHeight === false) share
 * the free space proportionally. Groups the user resized keep their
 * custom height as a proportional weight.
 * - When the sum of the groups' heights exceeds the container, passive
 * (non active-tab) groups are collapsed to header-only rows instead of
 * compressing content below its minimum height.
 * - Expanding a collapsed group keeps the interacted group open and takes
 * the vertical space from passive groups first (accordion behavior).
 * - The group holding the active tab has open priority: it is shrunk or
 * collapsed only as a last resort.
 * - Collapsed groups never disappear; they stay as single header rows.
 */

// Strip metrics mirrored from side-panel.css. The group resize handle
// overlaps its neighbours (6px tall with -3px margins) so it contributes no
// flow height; each in-flow drop zone strip does (8px, n of them per column
// — before the first group and between groups) while the trailing zone
// overlays the column's bottom edge and takes no flow height.
const DROP_ZONE_HEIGHT = 8;
const GROUP_RESIZE_HANDLE_FLOW_HEIGHT = 0;
const FALLBACK_HEADER_HEIGHT = 36;
const FALLBACK_MIN_HEIGHT = 90;
const FALLBACK_DEFAULT_HEIGHT = 200;

function getCssVarPx(name: string, fallback: number): number {
    if (typeof window === 'undefined') return fallback;
    try {
        const val = getComputedStyle(document.documentElement).getPropertyValue(
            name,
        );
        if (val) {
            const num = parseFloat(val);
            if (!isNaN(num)) return num;
        }
    } catch {}
    return fallback;
}

interface FitContext {
    containerHeight: number;
    headerHeight: number;
    minHeight: number;
    defaultHeight: number;
    activeTab: string;
}

function getVisible(groups: PanelGroup[]): PanelGroup[] {
    return groups.filter((g) => g.isVisible);
}

/** Rendered flow height of a single group. */
function renderedHeight(group: PanelGroup, ctx: FitContext): number {
    if (group.isMinimized) return ctx.headerHeight;
    return Math.max(ctx.minHeight, group.height || ctx.minHeight);
}

/** Fixed chrome (drop zones + resize handles) for a column of n groups.
    Only the before-first and between-group zones take flow height; the
    trailing zone overlays the panel's bottom edge. */
function chromeHeight(count: number): number {
    return (
        count * DROP_ZONE_HEIGHT + count * GROUP_RESIZE_HANDLE_FLOW_HEIGHT
    );
}

/** Total rendered height of the whole column including chrome. */
function columnTotal(groups: PanelGroup[], ctx: FitContext): number {
    const visible = getVisible(groups);
    if (visible.length === 0) return 0;
    return (
        visible.reduce((sum, g) => sum + renderedHeight(g, ctx), 0) +
        chromeHeight(visible.length)
    );
}

function findActiveGroupId(
    groups: PanelGroup[],
    activeTab: string,
): string | undefined {
    if (!activeTab) return undefined;
    return groups.find(
        (g) => g.isVisible && g.tabs.some((t) => t.id === activeTab),
    )?.id;
}

/**
 * Distribute the free container height over the open groups.
 *
 * Groups with a custom height keep it as a proportional weight; groups
 * without one (isCustomHeight === false) have no preference and share
 * whatever is left. The result always fills the container exactly, so the
 * bottom-most group reaches the end of the panel.
 */
function applyProportions(groups: PanelGroup[], ctx: FitContext): PanelGroup[] {
    const visible = getVisible(groups);
    const open = visible.filter((g) => !g.isMinimized);
    if (open.length === 0 || ctx.containerHeight <= 0) return groups;
    const collapsedCount = visible.length - open.length;
    const free = Math.max(
        open.length * ctx.minHeight,
        ctx.containerHeight -
            collapsedCount * ctx.headerHeight -
            chromeHeight(visible.length),
    );
    const weights = open.map((g) =>
        g.isCustomHeight && g.height && g.height > 0 ? g.height : 0,
    );
    const weightTotal = weights.reduce((a, b) => a + b, 0);
    const indexOf = new Map(open.map((g, i) => [g.id, i]));
    const sizes: number[] = new Array(open.length).fill(0);
    if (weightTotal > 0 && free > weightTotal) {
        // Custom heights fit with room to spare: distribute the surplus over
        // the groups without a user-defined height.
        const surplus = free - weightTotal;
        const noPref = weights
            .map((w, i) => (w === 0 ? i : -1))
            .filter((i) => i >= 0);
        if (noPref.length > 0) {
            const share = surplus / noPref.length;
            open.forEach((_, i) => {
                sizes[i] =
                    weights[i] > 0
                        ? weights[i]
                        : Math.max(ctx.minHeight, share);
            });
        } else {
            // Every group has a custom height: grow them proportionally.
            open.forEach((_, i) => {
                sizes[i] = weights[i] + surplus * (weights[i] / weightTotal);
            });
        }
    } else {
        // Shrink (or no preferences at all): pure proportional split.
        const denom = weightTotal > 0 ? weightTotal : open.length;
        open.forEach((_, i) => {
            const w = weights[i] > 0 ? weights[i] : 1;
            sizes[i] = Math.max(ctx.minHeight, (free * w) / denom);
        });
    }
    let changed = false;
    // Absorb the rounding remainder in the bottom-most open group so the
    // column total matches the container exactly (panel ends flush).
    const rounded = sizes.map((s) => Math.round(s));
    const drift = Math.round(free) - rounded.reduce((a, b) => a + b, 0);
    if (drift !== 0) {
        for (let i = rounded.length - 1; i >= 0; i--) {
            if (rounded[i] + drift >= ctx.minHeight) {
                rounded[i] += drift;
                break;
            }
        }
    }
    const next = groups.map((group) => {
        const i = indexOf.get(group.id);
        if (i === undefined) return group;
        const size = rounded[i];
        if (group.height !== size) {
            changed = true;
            return { ...group, height: size };
        }
        return group;
    });
    return changed ? next : groups;
}

/**
 * Expand `groupId` inside `groups`, taking the required vertical space from
 * passive groups (bottom-up).
 *
 * Photoshop-style priority of sacrifice:
 * 1. Uncustomized passive groups collapse first (bottom-up).
 * 2. Customized passive groups shrink to minHeight.
 * 3. Customized passive groups collapse (bottom-up).
 * 4. Active group shrinks toward minHeight.
 * 5. Active group collapses only if unavoidable.
 * 6. Target group shrinks toward minHeight only as a last resort.
 * Finishes with a proportional pass so the column still fills the container exactly.
 */
function applyExpand(
    groups: PanelGroup[],
    groupId: string,
    ctx: FitContext,
): PanelGroup[] {
    const targetIndex = groups.findIndex((g) => g.id === groupId);
    if (targetIndex === -1 || !groups[targetIndex].isVisible) return groups;
    const next = groups.map((g) => ({ ...g }));
    const target = next[targetIndex];
    target.isMinimized = false;
    target.isAutoCollapsed = false;

    // Restore previous height if available, or keep existing height if valid, or fallback
    const targetDesiredHeight =
        target.previousHeight && target.previousHeight > ctx.headerHeight
            ? target.previousHeight
            : target.height && target.height > ctx.headerHeight
              ? target.height
              : ctx.defaultHeight;
    target.height = Math.max(ctx.minHeight, targetDesiredHeight);

    const activeGroupId = findActiveGroupId(next, ctx.activeTab);
    const overflow = () => columnTotal(next, ctx) - ctx.containerHeight;

    const collapsePassive = (ids: string[]) => {
        for (const id of ids) {
            if (overflow() <= 0) break;
            const g = next.find((x) => x.id === id);
            if (!g || g.isMinimized) continue;
            // Remember current height before collapsing so it can be restored
            if (g.height && g.height > ctx.headerHeight) {
                g.previousHeight = g.height;
            }
            g.isMinimized = true;
            g.isAutoCollapsed = true;
        }
    };

    const shrinkPassive = (ids: string[]) => {
        for (const id of ids) {
            if (overflow() <= 0) break;
            const g = next.find((x) => x.id === id);
            if (!g || g.isMinimized) continue;
            const current = Math.max(ctx.minHeight, g.height || ctx.minHeight);
            const needed = overflow();
            const reducible = current - ctx.minHeight;
            if (reducible > 0) {
                const reduction = Math.min(reducible, needed);
                g.height = current - reduction;
            }
        }
    };

    const openPassiveBottomUp = (filter: (g: PanelGroup) => boolean) =>
        next
            .filter(
                (g) =>
                    g.isVisible &&
                    !g.isMinimized &&
                    g.id !== groupId &&
                    g.id !== activeGroupId &&
                    filter(g),
            )
            .map((g) => g.id)
            .reverse();

    // 1) Passive groups shrink toward minHeight (bottom-up)
    shrinkPassive(openPassiveBottomUp(() => true));

    // 2) Active group shrinks toward minHeight
    if (activeGroupId && activeGroupId !== groupId) {
        const active = next.find((x) => x.id === activeGroupId);
        if (active && !active.isMinimized && overflow() > 0) {
            const current = Math.max(
                ctx.minHeight,
                active.height || ctx.minHeight,
            );
            const needed = overflow();
            const reducible = current - ctx.minHeight;
            if (reducible > 0) {
                active.height = Math.max(
                    ctx.minHeight,
                    current - Math.min(reducible, needed),
                );
            }
        }
    }

    // 3) ONLY IF STILL OVERFLOWING: Passive groups collapse because they cannot maintain minHeight (90px)
    if (overflow() > 0) {
        collapsePassive(openPassiveBottomUp((g) => !g.isCustomHeight));
        collapsePassive(openPassiveBottomUp((g) => !!g.isCustomHeight));
    }

    // 4) Active group collapses only as an absolute last resort
    if (activeGroupId && activeGroupId !== groupId && overflow() > 0) {
        collapsePassive([activeGroupId]);
    }

    // 6) The target group yields size only after everything else is exhausted
    if (overflow() > 0) {
        const current = Math.max(ctx.minHeight, target.height || ctx.minHeight);
        target.height = Math.max(ctx.minHeight, current - overflow());
    }

    // 7) Fill the column exactly: bottom group reaches the panel end
    return applyProportions(next, ctx);
}

/**
 * Keep the column inside the container (collapsing passive groups on
 * overflow) and fill it completely (proportional heights). Returns null
 * when no changes are required.
 */
function computeFit(
    groups: PanelGroup[],
    ctx: FitContext,
): PanelGroup[] | null {
    const visible = getVisible(groups);
    if (visible.length === 0 || ctx.containerHeight <= 0) return null;
    const activeGroupId = findActiveGroupId(groups, ctx.activeTab);
    let next = groups.map((g) => ({ ...g }));
    let changed = false;

    const getNetOpenSpace = (list: PanelGroup[]) => {
        const vis = getVisible(list);
        const openCount = vis.filter((g) => !g.isMinimized).length;
        const collapsedCount = vis.length - openCount;
        return (
            ctx.containerHeight -
            collapsedCount * ctx.headerHeight -
            chromeHeight(vis.length)
        );
    };

    // A) RE-EXPAND PASS (Photoshop auto-collapse recovery):
    // Reopen auto-collapsed groups ONLY if there is genuinely unused slack space
    // in the container (e.g. window expanded or user shrank another group)
    // without forcing other open groups to shrink.
    let currentTotal = columnTotal(next, ctx);
    let unusedSlack = ctx.containerHeight - currentTotal;
    const minSlackToReopen = ctx.minHeight - ctx.headerHeight;

    if (unusedSlack >= minSlackToReopen) {
        const autoCollapsed = next.filter(
            (g) => g.isVisible && g.isMinimized && g.isAutoCollapsed,
        );

        for (const g of autoCollapsed) {
            if (unusedSlack < minSlackToReopen) break;

            const desired =
                g.previousHeight && g.previousHeight >= ctx.minHeight
                    ? g.previousHeight
                    : ctx.defaultHeight;
            const targetHeight = Math.max(
                ctx.minHeight,
                Math.min(desired, unusedSlack + ctx.headerHeight),
            );

            g.isMinimized = false;
            g.isAutoCollapsed = false;
            g.isCustomHeight = false;
            g.height = targetHeight;

            const consumed = targetHeight - ctx.headerHeight;
            unusedSlack -= consumed;
            changed = true;
        }
    }

    // B) OVERFLOW RESOLUTION & STRICT FIT TO CONTAINER HEIGHT:
    // 1) First shrink open groups toward minHeight (90px) bottom-up
    let overflow = columnTotal(next, ctx) - ctx.containerHeight;
    if (overflow > 0) {
        const openPassive = next
            .filter(
                (g) => g.isVisible && !g.isMinimized && g.id !== activeGroupId,
            )
            .reverse();

        for (const g of openPassive) {
            if (overflow <= 0) break;
            const curH = g.height || ctx.minHeight;
            const reducible = Math.max(0, curH - ctx.minHeight);
            if (reducible > 0) {
                const shrink = Math.min(reducible, overflow);
                g.height = curH - shrink;
                overflow -= shrink;
                changed = true;
            }
        }

        if (overflow > 0 && activeGroupId) {
            const active = next.find(
                (g) => g.id === activeGroupId && g.isVisible && !g.isMinimized,
            );
            if (active) {
                const curH = active.height || ctx.minHeight;
                const reducible = Math.max(0, curH - ctx.minHeight);
                if (reducible > 0) {
                    const shrink = Math.min(reducible, overflow);
                    active.height = curH - shrink;
                    overflow -= shrink;
                    changed = true;
                }
            }
        }
    }

    // 2) STRICT FIT ENFORCEMENT:
    // If open groups cannot fit inside containerHeight even at 90px (cannot maintain minHeight),
    // auto-collapse passive groups (bottom-up) until everything strictly fits inside containerHeight.
    while (true) {
        const vis = getVisible(next);
        const openGroups = vis.filter((g) => !g.isMinimized);
        if (openGroups.length <= 1) break;

        const netSpace = getNetOpenSpace(next);
        const minRequired = openGroups.length * ctx.minHeight;

        if (
            netSpace >= minRequired &&
            columnTotal(next, ctx) <= ctx.containerHeight
        ) {
            break;
        }

        const victim =
            openGroups.filter((g) => g.id !== activeGroupId).reverse()[0] ||
            openGroups[openGroups.length - 1];

        if (victim) {
            if (victim.height && victim.height > ctx.headerHeight) {
                victim.previousHeight = victim.height;
            }
            victim.isMinimized = true;
            victim.isAutoCollapsed = true;
            changed = true;
        } else {
            break;
        }
    }

    // Emergency: if all groups became minimized, reopen active or last
    if (!getVisible(next).some((g) => !g.isMinimized)) {
        const target =
            (activeGroupId && next.find((g) => g.id === activeGroupId)) ||
            getVisible(next)[getVisible(next).length - 1];
        if (target) {
            target.isMinimized = false;
            target.isAutoCollapsed = false;
            target.height = Math.max(ctx.minHeight, ctx.defaultHeight);
            changed = true;
        }
    }

    // C) Always-fill proportional pass (ensures exact flush fit to container bottom)
    const fitted = applyProportions(next, ctx);
    if (fitted !== next) {
        next = fitted;
        changed = true;
    }
    return changed ? next : null;
}

export interface UsePanelAutoCollapseOptions {
    enabled: boolean;
    groups: PanelGroup[];
    setPanelGroups: Dispatch<SetStateAction<PanelGroup[]>>;
    activeTab: string;
    minHeight?: number;
    defaultHeight?: number;
}

export interface PanelAutoCollapseController {
    containerRef: React.Ref<HTMLDivElement>;
    isGroupCollapsed: (groupId: string) => boolean;
    handleGroupHeaderActivate: (groupId: string) => void;
    requestGroupExpand: (groupId: string) => void;
}

export function usePanelAutoCollapse({
    enabled,
    groups,
    setPanelGroups,
    activeTab,
    minHeight,
    defaultHeight,
}: UsePanelAutoCollapseOptions): PanelAutoCollapseController {
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerHeight, setContainerHeight] = useState(0);
    const headerHeightRef = useRef(FALLBACK_HEADER_HEIGHT);
    // Bumped after a group/panel resize drag ends so the fit pass can run
    // against the final heights (live passes are skipped while resizing).
    const [resizeSettle, setResizeSettle] = useState(0);
    const metricsRef = useRef<FitContext>({
        containerHeight: 0,
        headerHeight: FALLBACK_HEADER_HEIGHT,
        minHeight: minHeight ?? FALLBACK_MIN_HEIGHT,
        defaultHeight: defaultHeight ?? FALLBACK_DEFAULT_HEIGHT,
        activeTab: '',
    });
    metricsRef.current = {
        containerHeight,
        headerHeight: headerHeightRef.current,
        minHeight: minHeight ?? FALLBACK_MIN_HEIGHT,
        defaultHeight: defaultHeight ?? FALLBACK_DEFAULT_HEIGHT,
        activeTab,
    };

    // Sync before the first layout pass: useLayoutEffect always runs before
    // any passive effect, so computeFit can never use a stale header height.
    useLayoutEffect(() => {
        headerHeightRef.current = getCssVarPx(
            '--panel-group-header-height',
            FALLBACK_HEADER_HEIGHT,
        );
    }, []);

    useEffect(() => {
        if (!enabled) return;
        const el = containerRef.current;
        if (!el) return;
        const update = () => setContainerHeight(el.clientHeight);
        update();
        const observer = new ResizeObserver(update);
        observer.observe(el);
        return () => observer.disconnect();
    }, [enabled]);

    useEffect(() => {
        if (!enabled) return;
        const handleMouseUp = () => {
            if (document.body.classList.contains('panel-resizing')) {
                setTimeout(() => setResizeSettle((c) => c + 1), 0);
            }
        };
        window.addEventListener('mouseup', handleMouseUp, true);
        return () => window.removeEventListener('mouseup', handleMouseUp, true);
    }, [enabled]);

    // Always-fill + auto-collapse pass: run whenever groups or the available
    // height change so the column always fills the panel exactly.
    useLayoutEffect(() => {
        if (!enabled || containerHeight <= 0) return;
        if (document.body.classList.contains('panel-resizing')) return;
        setPanelGroups((prev) => {
            const next = computeFit(prev, metricsRef.current);
            return next ?? prev;
        });
    }, [enabled, containerHeight, resizeSettle, groups, setPanelGroups]);

    // Accordion on activation: activating a tab (by click, menu command or
    // restore) expands the group that owns it.
    const requestGroupExpand = useCallback(
        (groupId: string) => {
            setPanelGroups((prev) => {
                const group = prev.find((g) => g.id === groupId);
                if (!group || !group.isVisible || !group.isMinimized)
                    return prev;
                return applyExpand(prev, groupId, metricsRef.current);
            });
        },
        [setPanelGroups],
    );

    const prevActiveTabRef = useRef<string | null>(null);
    useEffect(() => {
        if (!enabled || !activeTab) return;
        // Only trigger expansion when activeTab actually changes by user interaction,
        // never on groups/resize re-renders!
        if (prevActiveTabRef.current === activeTab) return;
        prevActiveTabRef.current = activeTab;

        const owner = groups.find(
            (g) => g.isVisible && g.tabs.some((t) => t.id === activeTab),
        );
        if (owner && owner.isMinimized) {
            requestGroupExpand(owner.id);
        }
    }, [enabled, activeTab, groups, requestGroupExpand]);

    // Header toggle with the auto-collapse guard: collapsing always fits;
    // expanding may need to reclaim space from passive groups first.
    const handleGroupHeaderActivate = useCallback(
        (groupId: string) => {
            setPanelGroups((prev) => {
                const group = prev.find((g) => g.id === groupId);
                if (!group || !group.isVisible) return prev;
                if (group.isMinimized) {
                    return applyExpand(prev, groupId, metricsRef.current);
                }
                return prev.map((g) =>
                    g.id === groupId
                        ? {
                              ...g,
                              isMinimized: true,
                              isAutoCollapsed: false,
                              previousHeight:
                                  g.height &&
                                  g.height > metricsRef.current.headerHeight
                                      ? g.height
                                      : g.previousHeight,
                          }
                        : g,
                );
            });
        },
        [setPanelGroups],
    );

    const isGroupCollapsed = useCallback(
        (groupId: string) => {
            return groups.find((g) => g.id === groupId)?.isMinimized ?? false;
        },
        [groups],
    );

    return {
        containerRef,
        isGroupCollapsed,
        handleGroupHeaderActivate,
        requestGroupExpand,
    };
}
